import { useTranslation } from 'react-i18next';
import { useState, useReducer, useEffect } from 'react';
import { Transforms, Editor as SlateEditor, Element as SlateElement } from 'slate';
import { useSlate } from 'slate-react';
import { Loader } from 'app/components';
import { v4 as uuidv4 } from 'uuid';
import { useGetDocumentTableOfContent } from 'entities/document';
import { i18n, i18nDynamic } from 'shared/i18n';
import { Dropdown, FilterButton } from 'app/components';
import { useEditorLocale } from 'app/api/documents/useEditorData';
import { useEditorDocument } from 'app/api/documents/useEditorData';
import { useEditorCaret } from 'app/state/contexts/EditorUiContext';
import { TableOfContentElement } from 'slate/types/slate';
import { defaultParagraph } from 'slate/extensions/paragraph';

const levels = ['h1', 'h2', 'h3', 'h4'];
const startValues = ['DOCUMENT_START', 'TOC_POSITION', 'MANUAL'];
const stopValues = ['DOCUMENT_END', 'TOC_NEXT', 'MANUAL'];

interface TocHeaderNode {
  uuid: string;
  type: string;
  name?: string;
  pageNumber?: number;
  revisionId?: string;
  children: TocHeaderNode[];
  [key: string]: unknown;
}

interface TocOverride {
  uuid: string;
  revisionId?: string;
  text?: string | null;
  excluded?: boolean;
}

interface TocSettings {
  start?: string | number;
  stop?: string | number;
  levels: string[];
  overrides: TocOverride[];
  uuid?: string;
  [key: string]: unknown;
}

interface TocData {
  content?: TocHeaderNode[];
  range: { start: number; stop: number };
}

interface DropdownItem {
  title: string;
  value: string;
}

interface ImportToCProps {
  onClose: (...args: unknown[]) => void;
  uuid?: string;
  defaultSettings?: Partial<TocSettings>;
  element?: TableOfContentElement;
}

export const ImportToC = ({ onClose, uuid, defaultSettings = {}, element }: ImportToCProps) => {
  const { t } = useTranslation(['common', 'toc']);

  const editor = useSlate();
  const reduxActiveNode = useEditorCaret().activeNode as { uuid?: string };
  const { data: pDocument } = useEditorDocument();
  const locale = useEditorLocale();

  const [selectedLevels, setSelectedLevels] = useState(defaultSettings.levels || levels);
  const [selectedStartType, setSelectedStartType] = useState(getStartType(defaultSettings));
  const [selectedStartManual, setSelectedStartManual] = useState(getManualStart(defaultSettings));
  const [selectedStopType, setSelectedStopType] = useState(getStopType(defaultSettings));
  const [selectedStopManual, setSelectedStopManual] = useState(getManualStop(defaultSettings));
  const [overrides, updateOverride] = useReducer(overrideReducer, defaultSettings.overrides || []);

  const settings: TocSettings = {
    start: getStart(selectedStartType, selectedStartManual),
    stop: getStop(selectedStopType, selectedStopManual),
    levels: selectedLevels,
    overrides,
  };

  const { data } = useGetDocumentTableOfContent(pDocument.revisionId, {
    start: settings.start as string,
    stop: settings.stop as string,
    currentPosition: (settings.uuid || reduxActiveNode.uuid) as string,
    headerHierarchy: selectedLevels,
    locale,
  }) as { data: TocData | undefined };

  const startItems = startValues.map((start) => ({
    title: i18nDynamic(`toc:settings.start.${start}`),
    value: start,
  }));

  const stopItems = stopValues.map((stop) => ({
    title: i18nDynamic(`toc:settings.stop.${stop}`),
    value: stop,
  }));

  useEffect(() => {
    if (selectedStopType === 'MANUAL') {
      setSelectedStopManual(data?.range.stop);
    }
  }, [selectedStopType, setSelectedStopManual, data?.range.stop]);

  const handleInsert = () => {
    if (element) {
      Transforms.setNodes(editor, { settings } as Partial<TableOfContentElement>, {
        at: [],
        match: (n) => n === element || (SlateElement.isElement(n) && n.type === 'table-of-content' && !!element.uuid && n.uuid === element.uuid),
      });
      onClose();
      return;
    }

    const tocNode: TableOfContentElement = {
      type: 'table-of-content',
      uuid: uuid || uuidv4(),
      revisionId: uuidv4(),
      previousRevisionId: null,
      children: [{ text: '' }],
      settings,
    };

    const point = SlateEditor.end(editor, editor.lastSelection);

    if (point) {
      Transforms.insertNodes(editor, [tocNode, defaultParagraph()], { at: point });
    }

    onClose();
  };

  const sortLevels = (arr: string[]) => arr.slice().sort((a, b) => levels.indexOf(a) - levels.indexOf(b));

  const handleFilter = (changeEvent: { checked: boolean; value: string }) => {
    const { checked, value } = changeEvent;
    setSelectedLevels((selected: string[]) => {
      const updated = checked ? [...selected, value] : selected.filter((s) => s !== value);
      return sortLevels(updated);
    });
  };

  const handleStartManual = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedStartManual(e.target.value);
  };

  const handleEndManual = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedStopManual(e.target.value);
  };

  if (!data?.content) {
    return <Loader />;
  }

  return (
    <>
      <div>
        <h2 className="subheading text-blue-600">{i18n('toc:hierarchy.levels')}</h2>
        <div className="d-flex gap-2">
          {levels.map((level) => (
            <FilterButton
              key={level}
              title={i18nDynamic(`toc:hierarchy.level.${level}`)}
              checked={selectedLevels.includes(level)}
              value={level}
              onChange={handleFilter}
            />
          ))}
        </div>
      </div>
      <hr className="my-5 border-gray-300" />
      <div>
        <h2 className="subheading text-blue-600 mt-4 mb-4">{i18n('toc:hierarchy.span')}</h2>
        <div className="row mb-3">
          <RangeSelector
            items={startItems}
            labelSuffix="start"
            onTypeChange={setSelectedStartType}
            onManualChange={handleStartManual}
            typeValue={selectedStartType}
            manualValue={settings.start}
            rangeValue={data.range.start}
          />
        </div>
        <div className="row">
          <RangeSelector
            items={stopItems}
            labelSuffix="stop"
            onTypeChange={setSelectedStopType}
            onManualChange={handleEndManual}
            typeValue={selectedStopType}
            manualValue={settings.stop}
            rangeValue={data.range.stop}
          />
        </div>
      </div>
      <hr className="my-5 border-gray-300" />
      <div className="mt-5">
        <HeaderList settings={settings} headers={data.content} selectedLevels={selectedLevels} updateOverride={updateOverride} />
      </div>
      <div className="d-flex justify-content-end mt-5">
        <button
          className="btn"
          onClick={(e) => {
            e.persist();
            onClose();
          }}
        >
          {t('common:button.cancel')}
        </button>
        <button className="btn btn-primary" onClick={handleInsert}>
          {t('toc:settings.insert')}
        </button>
      </div>
    </>
  );
};

interface RangeSelectorProps {
  items: DropdownItem[];
  labelSuffix: string;
  onTypeChange: (value: string) => void;
  onManualChange: React.ChangeEventHandler<HTMLInputElement>;
  typeValue: string;
  manualValue: string | number | undefined;
  rangeValue: number;
}

const RangeSelector = ({ items, labelSuffix, onTypeChange, onManualChange, typeValue, manualValue, rangeValue }: RangeSelectorProps) => {
  return (
    <>
      <div className="col align-items-stretch d-flex flex-column">
        <label className="form-label">{i18nDynamic(`toc:hierarchy.span.${labelSuffix}`)}</label>
        <Dropdown items={items} onChange={(value) => onTypeChange(value as string)} value={typeValue} />
      </div>
      <div className="col-3">
        <label className="form-label">{i18n('toc:hierarchy.span.pagenumber')}</label>
        <input
          className="form-control"
          disabled={typeValue !== 'MANUAL'}
          type="number"
          value={typeValue === 'MANUAL' && manualValue ? manualValue : rangeValue}
          onChange={onManualChange}
        />
      </div>
    </>
  );
};

interface HeaderListProps {
  selectedLevels: string[];
  updateOverride: (action: TocOverride) => void;
  settings: TocSettings;
  headers: TocHeaderNode[];
}

const HeaderList = ({ selectedLevels, updateOverride, settings, headers }: HeaderListProps) => {
  return (
    <>
      <div className="row align-items-center mb-3">
        <div className="col-6">
          <h3 className="subheading text-blue-600">{i18n('toc:hierarchy.headers')}</h3>
        </div>
        <div className="col-6">
          <h3 className="subheading text-blue-600">{i18n('toc:hierarchy.override')}</h3>
        </div>
      </div>
      {headers.map((header, idx) => (
        <HeaderItem
          key={idx}
          headers={headers}
          header={header}
          selectedLevels={selectedLevels}
          overrides={settings.overrides}
          updateOverride={updateOverride}
          {...header}
        />
      ))}
    </>
  );
};

interface HeaderItemProps {
  selectedLevels: string[];
  header: TocHeaderNode;
  headers: TocHeaderNode[];
  updateOverride: (action: TocOverride) => void;
  overrides: TocOverride[];
  parentExcluded?: boolean;
}

const HeaderItem = ({ selectedLevels, header, headers, updateOverride, overrides, parentExcluded = false }: HeaderItemProps) => {
  const { t } = useTranslation(['doc-editor', 'toc']);

  const existingOverride = overrides.find((override) => override.uuid === header.uuid);
  const defaultOverride = overrides?.find((override) => header.uuid === override.uuid)?.text || '';
  const initialIsExcluded = existingOverride ? !!existingOverride.excluded : false;

  const [overrideName, setOverrideName] = useState(defaultOverride);
  const [isExcluded, setIsExcluded] = useState(initialIsExcluded);

  const handleOverrideName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.currentTarget.value;
    setOverrideName(text);
    updateOverride({ uuid: header.uuid, revisionId: header.revisionId, text: text !== '' ? text : null });
  };

  const handleExcludeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const excluded = !e.target.checked;
    setIsExcluded(excluded);
    updateOverride({ uuid: header.uuid, revisionId: header.revisionId, excluded: excluded, text: overrideName !== '' ? overrideName : null });
  };

  return (
    <>
      {selectedLevels.includes(header.type) ? (
        <>
          {isFirstOfPage(headers, header, selectedLevels) ? (
            <div className="row mt-3">
              <div className="col-12">
                <div className="fs-7 mb-1 fw-bolder text-blue-800">{i18n('toc:hierarchy.page-number') + ' ' + header.pageNumber}</div>
              </div>
            </div>
          ) : null}
          <div className="row align-items-center mb-2">
            <div className="col-6 d-flex align-items-center">
              <input
                type="checkbox"
                className="form-check-input ms-0 me-3 mt-0 flex-shrink-0"
                checked={!isExcluded && !parentExcluded}
                onChange={handleExcludeChange}
                role="switch"
              />
              <div className={isExcluded || parentExcluded ? 'text-decoration-line-through' : ''}>
                {header.name || t('toc:settings.header.no-permission')}
                <div className="text-blue-600">{i18nDynamic(`toc:hierarchy.level.${header.type}`)}</div>
              </div>
            </div>
            <div className="col-6">
              <input type="text" className="form-control" disabled={isExcluded || parentExcluded} value={overrideName} onChange={handleOverrideName} />
            </div>
          </div>
        </>
      ) : null}
      {header.children.map((child, idx) => (
        <HeaderItem
          key={idx}
          selectedLevels={selectedLevels}
          updateOverride={updateOverride}
          headers={headers}
          header={child}
          overrides={overrides}
          parentExcluded={isExcluded || parentExcluded}
        />
      ))}
    </>
  );
};

const overrideReducer = (overrides: TocOverride[], action: TocOverride) => {
  const { uuid, text, excluded } = action;
  const otherOverrides = overrides.filter((override) => override.uuid !== uuid);

  if (text === undefined && excluded === undefined) {
    return otherOverrides;
  } else {
    const newOverride = { uuid, ...(text !== undefined && { text }), ...(excluded !== undefined && { excluded }) };
    return [...otherOverrides, newOverride];
  }
};

const isFirstOfPage = (headers: TocHeaderNode[], header: TocHeaderNode, selectedLevels: string[]) => {
  const pageNumber = header.pageNumber;
  const flatHeaders = headers.flatMap(flatten);
  const filteredHeaders = flatHeaders.filter((header) => selectedLevels.includes(header.type));
  const previousIndex = filteredHeaders.findIndex((fh) => fh === header) - 1;
  const previousPageNumber = filteredHeaders[previousIndex]?.pageNumber;

  return pageNumber !== previousPageNumber;
};

const flatten = (header: TocHeaderNode): TocHeaderNode[] => [header, ...header.children.flatMap(flatten)];

const getStart = (start: string | number | undefined, startManual: string | number | undefined) => {
  return start !== 'MANUAL' ? start : startManual;
};
const getStop = (stop: string | number | undefined, stopManual: string | number | undefined) => {
  return stop !== 'MANUAL' ? stop : stopManual;
};

const getStartType = (settings: Partial<TocSettings>) => {
  if (!settings.start) {
    return 'DOCUMENT_START';
  }
  return isNaN(Number(settings.start)) ? String(settings.start) : 'MANUAL';
};

const getStopType = (settings: Partial<TocSettings>) => {
  if (!settings.start) {
    return 'DOCUMENT_END';
  }
  return isNaN(Number(settings.stop)) ? String(settings.stop) : 'MANUAL';
};

const getManualStart = (settings: Partial<TocSettings>, defaultValue: number = 1) => {
  return isNaN(Number(settings.start)) ? defaultValue : settings.start;
};

const getManualStop = (settings: Partial<TocSettings>) => {
  return isNaN(Number(settings.stop)) ? '' : settings.stop;
};
